import { useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import {
  EventType,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { loginRequest, msalInstance } from './msalConfig';
import { hasFrontlineSession } from './frontlineSession';
import TeamsSDK from '@/teams/TeamsSDK';
import { withBase } from '@/utils/basePath';

// MSAL real SOLO cuando VITE_AUTH_ENABLED=true. Si no, modo demo (sin login real), igual que hoy.
// Excluyente con Easy Auth: al encender MSAL hay que APAGAR Easy Auth en el App Service.
const AUTH_ENABLED = import.meta.env.VITE_AUTH_ENABLED === 'true';

/**
 * RF-002 — Rutas de acceso Frontline: públicas, y NO deben forzar el `loginRedirect` de MSAL.
 *
 * 🔴 **Toda ruta pública del router tiene que estar TAMBIÉN aquí**, y esta lista existe como
 * constante única precisamente porque no lo estuvo: `/app/restablecer` se declaró en `AppRouter` y
 * se olvidó en las DOS copias que este fichero tenía de la lista, así que quien abría el enlace de
 * «olvidé mi contraseña» acababa en `/login` — o sea, la única persona para la que ese enlace
 * existe (alguien SIN sesión, por definición) era la única que no podía usarlo. Visto en QA el
 * 2026-08-18.
 *
 * Lo vigila `rutasPublicasCoinciden.test.ts`, que lee `AppRouter.tsx` y compara.
 */
export const RUTAS_DE_ACCESO_ANONIMO = [
  '/login',
  '/app/registro',
  '/app/activar',
  '/app/restablecer',
] as const;

const esRutaDeAccesoAnonimo = (pathname: string) =>
  RUTAS_DE_ACCESO_ANONIMO.some(ruta => pathname.endsWith(ruta));

interface Props {
  children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [ready, setReady] = useState(false);
  const [inTeams, setInTeams] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await msalInstance.initialize();
      // Procesa el #code del redirect de login -> intercambia por tokens -> cachea la cuenta.
      // SIN esto el #code se queda pegado en la URL y nunca hay sesión -> token vacío -> 401.
      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) msalInstance.setActiveAccount(redirectResult.account);
      // Restaurar cuenta activa (tras un redirect de login o una recarga)
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) msalInstance.setActiveAccount(accounts[0]);
      msalInstance.addEventCallback(event => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const account = (event.payload as AuthenticationResult).account;
          if (account) msalInstance.setActiveAccount(account);
        }
      });
      // Detectar Teams: dentro del tab el redirect MSAL se bloquea (iframe) -> usar Teams SSO.
      // 2026-09-04 — antes armaba aquí su propia carrera de 2s, duplicada e independiente de la
      // que también corría en `usePlatform.ts`; ambas consumen ahora el mismo punto único
      // (`TeamsSDK.detectTeams`, cacheado a nivel módulo — ver su doc-comment). `markInTeams()` ya
      // lo llama `detectTeams` internamente cuando el contexto resuelve.
      const teamsCtx = await TeamsSDK.detectTeams();
      const teams = teamsCtx !== null;
      // SSO SILENCIOSO: si NO hay cuenta pero SÍ existe una sesión activa en Microsoft (cookie de AAD),
      // autenticar sin interacción — un usuario Sigma ya logueado NO ve la pantalla de login. Si no hay
      // sesión, ssoSilent falla en silencio y el render de abajo manda a /login. No corre en /login,
      // /app/registro ni con sesión Frontline (ahí el usuario decide explícitamente).
      if (AUTH_ENABLED && !teams && msalInstance.getAllAccounts().length === 0) {
        const p = window.location.pathname;
        if (!esRutaDeAccesoAnonimo(p) && !hasFrontlineSession()) {
          try {
            const silent = await msalInstance.ssoSilent(loginRequest);
            if (silent?.account) msalInstance.setActiveAccount(silent.account);
          } catch { /* sin sesión AAD activa → se mostrará /login */ }
        }
      }
      if (!cancelled) { setInTeams(teams); setReady(true); }
    })().catch(() => {
      if (!cancelled) setReady(true); // no bloquear el render si algo falla
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return null;

  // RF-002 — Rutas de acceso Frontline (usuarios SIN Azure AD): NO deben forzar el loginRedirect de
  // MSAL. Ver RUTAS_DE_ACCESO_ANONIMO. Y si YA hay sesión Frontline, el usuario no está en Entra ID,
  // así que redirigirlo a Microsoft lo dejaría afuera.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isFrontlineAccessRoute = esRutaDeAccesoAnonimo(path);

  // Modo demo (sin MSAL) O dentro de Teams: no forzar el loginRedirect.
  // En Teams la auth es por SSO (getAuthToken en client.ts); el redirect rompería en el iframe.
  if (!AUTH_ENABLED || inTeams || isFrontlineAccessRoute || hasFrontlineSession()) {
    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
  }

  // Navegador standalone con MSAL on y ruta protegida: si NO hay sesión corporativa, mandar a /login
  // —la página con AMBAS opciones (SSO de Sigma + usuario/contraseña Frontline)— en vez de auto-
  // redirigir a Microsoft (que forzaría SSO a todos y se saltaría el login Frontline). El botón "Entra"
  // de /login dispara el loginRedirect real; el #code de vuelta lo procesa el efecto de arriba.
  if (msalInstance.getAllAccounts().length === 0) {
    window.location.replace(withBase('/login'));
    return null;
  }

  // Hay cuenta corporativa (MSAL) → render normal de la app.
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
